import React, { useState, useEffect } from 'react';
import { employerAPI } from '../services/api';
import toast from 'react-hot-toast';
import { downloadResumeAuthenticated } from '../utils/resumeUrl';

const ApplicationList = ({ jobId = null }) => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const pageSize = 10;

  useEffect(() => {
    fetchApplications();
  }, [page, jobId]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = jobId
        ? await employerAPI.getJobApplications(jobId, page, pageSize)
        : await employerAPI.getApplications(page, pageSize);
      
      setApplications(response.data.applications || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'N/A';
    }
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return 'N/A';
    }
  };

  const getCandidateName = (application) => {
    // Try multiple field variations
    if (application.candidateName) return application.candidateName;
    if (application.candidate_name) return application.candidate_name;
    
    // Try to construct from firstName and lastName
    const firstName = application.candidateFirstName || application.candidate_first_name || application.firstName || application.first_name;
    const lastName = application.candidateLastName || application.candidate_last_name || application.lastName || application.last_name;
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }
    if (firstName) return firstName;
    if (lastName) return lastName;
    
    // Fallback to email if available
    const email = application.candidateEmail || application.candidate_email || application.email;
    if (email) {
      const emailName = email.split('@')[0];
      return emailName.charAt(0).toUpperCase() + emailName.slice(1);
    }
    
    return 'Anonymous Candidate';
  };

  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower === 'submitted') {
      return 'bg-blue-100 text-blue-800';
    } else if (statusLower === 'reviewed') {
      return 'bg-yellow-100 text-yellow-800';
    } else if (statusLower === 'accepted') {
      return 'bg-green-100 text-green-800';
    } else if (statusLower === 'rejected') {
      return 'bg-red-100 text-red-800';
    }
    return 'bg-gray-100 text-gray-800';
  };

  if (loading && applications.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {applications.length === 0 ? (
        <div className="bg-white p-12 rounded-xl shadow-sm border border-gray-200 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="mt-4 text-lg font-semibold text-gray-900">No applications found</h3>
          <p className="mt-2 text-sm text-gray-500">
            {jobId
              ? 'No applications have been submitted for this job yet.'
              : 'No applications have been submitted for your job postings yet.'}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {applications.map((application) => (
              <div
                key={application.id}
                className="ui-card-interactive p-6"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-lg font-bold text-gray-900">
                        {getCandidateName(application)}
                      </h3>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(
                          application.status
                        )}`}
                      >
                        {(application.status || 'submitted').toLowerCase()}
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-3">
                      <div className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                        <span>{application.candidateEmail || application.candidate_email || application.email || 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        <span>Applied {formatDate(application.appliedAt || application.applied_at || application.createdAt || application.created_at)}</span>
                      </div>
                    </div>

                    {!jobId && (application.jobTitle || application.job_title) && (
                      <div className="mb-3">
                        <p className="text-sm text-gray-600">
                          <strong>Job:</strong> {application.jobTitle || application.job_title}
                        </p>
                      </div>
                    )}

                    {/* Cover Letter Preview */}
                    {(application.coverLetter || application.cover_letter) && (
                      <div className="mb-3">
                        <p className="text-sm font-medium text-gray-700 mb-1">Cover Letter:</p>
                        <p className="text-sm text-gray-600 line-clamp-2">{application.coverLetter || application.cover_letter}</p>
                      </div>
                    )}

                    {/* Additional Info */}
                    {(application.expectedSalary || application.expected_salary || 
                      application.noticePeriod || application.notice_period ||
                      application.availabilityDate || application.availability_date) && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                          {(application.expectedSalary || application.expected_salary) && (
                            <div>
                              <span className="font-medium text-gray-700">Expected Salary:</span>{' '}
                              <span className="text-gray-900">
                                {typeof (application.expectedSalary || application.expected_salary) === 'number'
                                  ? (application.expectedSalary || application.expected_salary).toLocaleString()
                                  : application.expectedSalary || application.expected_salary}
                                {(application.expectedSalary || application.expected_salary) && (
                                  <span className="text-gray-500 text-xs ml-1">(per annum)</span>
                                )}
                              </span>
                            </div>
                          )}
                          {(application.noticePeriod || application.notice_period) && (
                            <div>
                              <span className="font-medium text-gray-700">Notice Period:</span>{' '}
                              <span className="text-gray-900">
                                {application.noticePeriod || application.notice_period}
                                {typeof (application.noticePeriod || application.notice_period) === 'number' && ' days'}
                              </span>
                            </div>
                          )}
                          {(application.availabilityDate || application.availability_date) && (
                            <div>
                              <span className="font-medium text-gray-700">Expected Joining date:</span>{' '}
                              <span className="text-gray-900">
                                {formatDateOnly(application.availabilityDate || application.availability_date)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Links */}
                    <div className="flex flex-wrap gap-3 mt-3">
                      {(application.resumeUrl || application.resume_url) && (
                        <button
                          type="button"
                          onClick={() =>
                            downloadResumeAuthenticated(
                              application.resumeUrl || application.resume_url
                            ).catch(() => toast.error('Could not download resume'))
                          }
                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors text-sm"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                            />
                          </svg>
                          View Resume
                        </button>
                      )}
                      {(application.portfolioUrl || application.portfolio_url) && (
                        <a
                          href={application.portfolioUrl || application.portfolio_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                            />
                          </svg>
                          View Portfolio
                        </a>
                      )}
                      <button
                        onClick={() => setSelectedApplication(application)}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {total > pageSize && (
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total}{' '}
                applications
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page * pageSize >= total}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Application Detail Modal */}
      {selectedApplication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">
                Application from {getCandidateName(selectedApplication)}
              </h2>
              <button
                onClick={() => setSelectedApplication(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Candidate Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Candidate Information</h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <strong>Name:</strong> {getCandidateName(selectedApplication)}
                  </div>
                  <div>
                    <strong>Email:</strong> {selectedApplication.candidateEmail || selectedApplication.candidate_email || selectedApplication.email || 'N/A'}
                  </div>
                  <div>
                    <strong>Applied:</strong> {formatDate(selectedApplication.appliedAt || selectedApplication.applied_at || selectedApplication.createdAt || selectedApplication.created_at)}
                  </div>
                  <div>
                    <strong>Status:</strong>{' '}
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadge(
                        selectedApplication.status
                      )}`}
                    >
                      {(selectedApplication.status || 'submitted').toLowerCase()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Job Info */}
              {(selectedApplication.jobTitle || selectedApplication.job_title) && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Job Applied For</h3>
                  <p className="text-sm text-gray-700">{selectedApplication.jobTitle || selectedApplication.job_title}</p>
                </div>
              )}

              {/* Cover Letter */}
              {(selectedApplication.coverLetter || selectedApplication.cover_letter) && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Cover Letter</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {selectedApplication.coverLetter || selectedApplication.cover_letter}
                    </p>
                  </div>
                </div>
              )}

              {/* Additional Details */}
              {(selectedApplication.expectedSalary || selectedApplication.expected_salary || 
                selectedApplication.noticePeriod || selectedApplication.notice_period ||
                selectedApplication.availabilityDate || selectedApplication.availability_date) && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Additional Details</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    {(selectedApplication.expectedSalary || selectedApplication.expected_salary) && (
                      <div className="flex items-start">
                        <span className="text-sm font-medium text-gray-700 min-w-[140px]">Expected Salary:</span>
                        <span className="text-sm text-gray-900">
                          {typeof (selectedApplication.expectedSalary || selectedApplication.expected_salary) === 'number'
                            ? (selectedApplication.expectedSalary || selectedApplication.expected_salary).toLocaleString()
                            : selectedApplication.expectedSalary || selectedApplication.expected_salary}
                          {(selectedApplication.expectedSalary || selectedApplication.expected_salary) && (
                            <span className="text-gray-500 text-xs ml-1">(per annum)</span>
                          )}
                        </span>
                      </div>
                    )}
                    {(selectedApplication.noticePeriod || selectedApplication.notice_period) && (
                      <div className="flex items-start">
                        <span className="text-sm font-medium text-gray-700 min-w-[140px]">Notice Period:</span>
                        <span className="text-sm text-gray-900">
                          {selectedApplication.noticePeriod || selectedApplication.notice_period}
                          {typeof (selectedApplication.noticePeriod || selectedApplication.notice_period) === 'number' && ' days'}
                        </span>
                      </div>
                    )}
                    {(selectedApplication.availabilityDate || selectedApplication.availability_date) && (
                      <div className="flex items-start">
                        <span className="text-sm font-medium text-gray-700 min-w-[140px]">Expected Joining date:</span>
                        <span className="text-sm text-gray-900">
                          {formatDateOnly(selectedApplication.availabilityDate || selectedApplication.availability_date)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Links */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Documents & Links</h3>
                <div className="flex flex-wrap gap-3">
                  {(selectedApplication.resumeUrl || selectedApplication.resume_url) ? (
                    <button
                      type="button"
                      onClick={() =>
                        downloadResumeAuthenticated(
                          selectedApplication.resumeUrl || selectedApplication.resume_url
                        ).catch(() => toast.error('Could not download resume'))
                      }
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                      View Resume
                    </button>
                  ) : (
                    <span className="text-sm text-gray-500">No resume provided</span>
                  )}
                  {(selectedApplication.portfolioUrl || selectedApplication.portfolio_url) ? (
                    <a
                      href={selectedApplication.portfolioUrl || selectedApplication.portfolio_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                      View Portfolio
                    </a>
                  ) : (
                    <span className="text-sm text-gray-500">No portfolio provided</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationList;

